import React, { useEffect, useState } from "react";
import { Button, Divider, Grid, Typography } from "@mui/material";
import CustomFormLabel from "src/components/forms/theme-elements/CustomFormLabel";
import ParentCard from "src/components/shared/ParentCard";
import Breadcrumb from "src/layouts/full/shared/breadcrumb/Breadcrumb";
import { useFormik } from "formik";
import * as yup from "yup";
import { useNavigate } from "react-router";
import CustomTextField from "src/components/forms/theme-elements/CustomTextField";
import { IconArrowBack, IconPlus } from "@tabler/icons";

import {
  Autocomplete,
  GoogleMap,
  Marker,
  DirectionsRenderer,
} from "@react-google-maps/api";
import swal from "sweetalert";
import RouteService from "src/service/route";
import { DeleteOutlineOutlined } from "@mui/icons-material";

const BCrumb = [
  {
    to: "/",
    title: "Home",
  },
  {
    title: " Route Setup ",
  },
];

const validationSchema = yup.object({
  route_name: yup.string("").required("Route Name is required"),
});

const AddNewRoute = () => {
  const sessionData = JSON.parse(localStorage.getItem("user"));
  const navigate = useNavigate();

  const [starting_place_name, setStarting_place_name] = useState("");
  const [starting_latitude, setStarting_latitude] = useState("");
  const [starting_longitude, setStarting_longitude] = useState("");

  const [ending_place_name, setEnding_place_name] = useState("");
  const [ending_latitude, setEnding_latitude] = useState("");
  const [ending_longitude, setEnding_longitude] = useState("");

  const [viaPoints, setViaPoints] = useState([]);

  const [distance, setDistance] = useState("");
  const [time_duration, setDuration] = useState("");
  const [directions, setDirections] = useState(null);

  const [startMarker, setStartMarker] = useState({
    position: { lat: 23.876536, lng: 85.876453 },
    address: "",
  });

  const [endMarker, setEndMarker] = useState({
    position: { lat: 23.876536, lng: 85.876453 },
    address: "",
  });

  const onAutocompleteLoadStart = (autocomplete) => {
    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();

      if (place.geometry) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();

        setStartMarker({
          position: { lat, lng },
          address: place.formatted_address,
        });

        setStarting_place_name(place.formatted_address);
        setStarting_latitude(lat);
        setStarting_longitude(lng);
      }
    });
  };

  const onAutocompleteLoadVia = (autocomplete, index) => {
    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();

      if (place.geometry) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();

        const updatedViaPoints = [...viaPoints];
        updatedViaPoints[index] = {
          ...viaPoints[index],
          via_latitude: lat,
          via_longitude: lng,
          address: place.formatted_address,
          via_name: place.formatted_address,
        };

        setViaPoints(updatedViaPoints);
      }
    });
  };

  const onAutocompleteLoadEnd = (autocomplete) => {
    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();

      if (place.geometry) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();

        setEndMarker({
          position: { lat, lng },
          address: place.formatted_address,
        });

        setEnding_place_name(place.formatted_address);
        setEnding_latitude(lat);
        setEnding_longitude(lng);
      }
    });
  };

  useEffect(() => {
    if (startMarker.address && endMarker.address) {
      calculateDirections();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startMarker, endMarker, viaPoints]);

  const addViaPoint = () => {
    setViaPoints([
      ...viaPoints,
      { via_name: "", via_latitude: "", via_longitude: "" },
    ]);
  };

  const handleViaPointChange = (index, action) => {
    setViaPoints((prevViaPoints) => {
      const updatedViaPoints = [...prevViaPoints];
      if (action === "delete") {
        // Remove the via point at the specified index
        updatedViaPoints.splice(index, 1);
      }
      return updatedViaPoints;
    });
  };

  const calculateDirections = () => {
    const directionsService = new window.google.maps.DirectionsService();
    let totalDistance = 0;
    let totalDuration = 0;

    const waypoints = [];

    // Collect waypoints
    for (let i = 0; i < viaPoints.length; i++) {
      waypoints.push({
        location: new window.google.maps.LatLng(
          parseFloat(viaPoints[i].via_latitude),
          parseFloat(viaPoints[i].via_longitude)
        ),
        stopover: true,
      });
    }

    // Request directions with waypoints
    directionsService.route(
      {
        origin: startMarker.address,
        destination: endMarker.address,
        travelMode: window.google.maps.TravelMode.DRIVING,
        waypoints: waypoints,
      },

      (result, status) => {
        if (status === window.google.maps.DirectionsStatus.OK) {
          setDirections(result);
          // Calculate total distance
          result.routes[0].legs.forEach((leg) => {
            totalDistance += leg.distance.value;
            totalDuration += leg.duration.value;
          });
          setDistance((totalDistance / 1000).toFixed(2) + " km");
          setDuration((totalDuration / 60).toFixed(0));
        } else {
          console.error("Directions request failed due to " + status);
          setDistance(0);
          setDuration(0);
        }
      }
    );
  };

  const initialValues = {
    route_name: "",
    start_pt_name: "",
    start_pt_latitude: "",
    start_pt_longitude: "",
    end_pt_name: "",
    end_pt_latitude: "",
    end_pt_longitude: "",
    distance: "",
    time_duration: "",
    c_by: sessionData.data.id,
    route_via: [],
  };

  const { values, errors, touched, handleChange, handleBlur, handleSubmit } =
    useFormik({
      initialValues: initialValues,
      validationSchema: validationSchema,
      onSubmit: async (values) => {
        const viaPointsData = viaPoints.map((viaPoint) => ({
          via_name: viaPoint.via_name,
          via_latitude: viaPoint.via_latitude,
          via_longitude: viaPoint.via_longitude,
        }));
        const payloadval = {
          route_name: values.route_name,
          start_pt_name: starting_place_name,
          start_pt_latitude: starting_latitude,
          start_pt_longitude: starting_longitude,
          end_pt_name: ending_place_name,
          end_pt_latitude: ending_latitude,
          end_pt_longitude: ending_longitude,
          distance: parseFloat(distance.replace(/[, ]/g, "")),
          time_duration: time_duration,
          c_by: sessionData.data.id,
          route_via: viaPointsData,
        };

        // alert("payloadval=", payloadval);
        try {
          const res = await RouteService.add_route(payloadval);

          swal("success", res.message, "success");

          setTimeout(() => {
            navigate("/route");
          }, 1000);
        } catch (error) {
          swal("error", error, "error");
        }
      },
    });

  return (
    <>
      <Breadcrumb title="Route" items={BCrumb} />
      <ParentCard title="" style={{ minHeight: "600px !important" }}>
        <Grid container alignItems="center">
          <Grid
            item
            xs={4}
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              gap: 2,
              marginBottom: "15px;",
            }}
          >
            <Typography
              style={{
                fontSize: "22px",
                fontWeight: "bold",
                fontFamily: "Sans-Serif",
              }}
              variant="h2"
            >
              Add Route
            </Typography>
          </Grid>

          <Grid
            item
            xs={8}
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              marginBottom: "15px;",
            }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                navigate("/route");
              }}
            >
              <IconArrowBack width={20} />
            </Button>
          </Grid>
        </Grid>
        <Divider />
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3} style={{ marginTop: "10px" }}>
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Grid item spacing={3} container lg={12} md={12} sm={12} xs={12}>
                <Grid
                  item
                  lg={4}
                  md={6}
                  sm={6}
                  xs={12}
                  style={{ marginTop: "-10px" }}
                >
                  <CustomFormLabel htmlFor="end_pt_latitude">
                    Start Place Name <span style={{ color: "red" }}>*</span>
                  </CustomFormLabel>
                  <Autocomplete onLoad={onAutocompleteLoadStart}>
                    <CustomTextField
                      type="text"
                      onChange={handleChange}
                      onBlur={(event, newValue) => {
                        handleBlur(event);
                      }}
                      variant="outlined"
                      placeholder="Enter starting address"
                      fullWidth
                    />
                  </Autocomplete>
                </Grid>

                <Grid
                  item
                  lg={4}
                  md={6}
                  sm={6}
                  xs={12}
                  style={{ marginTop: "-10px" }}
                >
                  <CustomFormLabel htmlFor="start_pt_latitude">
                    Latitude <span style={{ color: "red" }}>*</span>
                  </CustomFormLabel>
                  <CustomTextField
                    type="text"
                    value={starting_latitude}
                    variant="outlined"
                    // placeholder="Enter Starting Destination Latitude"
                    fullWidth
                    InputProps={{ readOnly: true }}
                  />
                </Grid>

                <Grid
                  item
                  lg={4}
                  md={6}
                  sm={6}
                  xs={12}
                  style={{ marginTop: "-10px" }}
                >
                  <CustomFormLabel htmlFor="start_pt_longitude">
                    Longitude <span style={{ color: "red" }}>*</span>
                  </CustomFormLabel>
                  <CustomTextField
                    id="start_pt_longitude"
                    name="start_pt_longitude"
                    type="text"
                    value={starting_longitude}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    variant="outlined"
                    // placeholder="Enter Starting Destination Longitude"
                    fullWidth
                    InputProps={{ readOnly: true }}
                  />
                </Grid>

                {/* ===================== via points code ===================== */}
                <Grid item lg={12} md={12} sm={12}>
                  {/* <Typography variant="h6">Via Points</Typography> */}
                </Grid>

                {viaPoints.map((viaPoint, index) => (
                  <React.Fragment key={index}>
                    <Grid
                      item
                      lg={4}
                      md={6}
                      sm={6}
                      xs={12}
                      style={{ marginTop: "-10px" }}
                    >
                      <CustomFormLabel htmlFor={`via_name_${index}`}>
                        Via Point Name
                      </CustomFormLabel>
                      <Autocomplete
                        onLoad={(autocomplete) =>
                          onAutocompleteLoadVia(autocomplete, index)
                        }
                      >
                        <CustomTextField
                          id={`via_name_${index}`}
                          name={`via_name_${index}`}
                          type="text"
                          // value={viaPoint.via_name}
                          onChange={(e) =>
                            handleViaPointChange(e, index, "via_name")
                          }
                          variant="outlined"
                          placeholder="Enter Via Point Name"
                          fullWidth
                        />
                      </Autocomplete>
                    </Grid>

                    <Grid
                      item
                      lg={3}
                      md={6}
                      sm={6}
                      xs={12}
                      style={{ marginTop: "-10px" }}
                    >
                      <CustomFormLabel htmlFor={`via_latitude_${index}`}>
                        Via Point Latitude
                      </CustomFormLabel>
                      <CustomTextField
                        id={`via_latitude_${index}`}
                        name={`via_latitude_${index}`}
                        type="text"
                        value={viaPoint.via_latitude}
                        onChange={(e) =>
                          handleViaPointChange(e, index, "via_latitude")
                        }
                        variant="outlined"
                        placeholder="Via Point Latitude"
                        fullWidth
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>

                    <Grid
                      item
                      lg={3}
                      md={6}
                      sm={6}
                      xs={12}
                      style={{ marginTop: "-10px" }}
                    >
                      <CustomFormLabel htmlFor={`via_longitude_${index}`}>
                        Via Point Longitude
                      </CustomFormLabel>
                      <CustomTextField
                        id={`via_longitude_${index}`}
                        name={`via_longitude_${index}`}
                        type="text"
                        value={viaPoint.via_longitude}
                        onChange={(e) =>
                          handleViaPointChange(e, index, "via_longitude")
                        }
                        variant="outlined"
                        placeholder="Via Point Longitude"
                        fullWidth
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>

                    <Grid
                      item
                      lg={2}
                      md={2}
                      sm={2}
                      xs={2}
                      style={{ marginTop: "15px" }}
                    >
                      {/* Add a button to delete the via point */}
                      <Button
                        variant="outlined"
                        onClick={() => handleViaPointChange(index, "delete")}
                        style={{ marginTop: "10px", color: "red" }}
                      >
                        <DeleteOutlineOutlined />
                      </Button>
                    </Grid>
                  </React.Fragment>
                ))}

                <Grid item lg={12} md={12} sm={12}>
                  <Button
                    variant="outlined"
                    onClick={addViaPoint}
                    style={{ float: "right" }}
                  >
                    <IconPlus />
                  </Button>
                </Grid>
                {/* ===================== via points code ends ================ */}

                <Grid
                  item
                  lg={4}
                  md={6}
                  sm={6}
                  xs={12}
                  style={{ marginTop: "-10px" }}
                >
                  <CustomFormLabel htmlFor="end_pt_latitude">
                    End Place Name <span style={{ color: "red" }}>*</span>
                  </CustomFormLabel>
                  <Autocomplete onLoad={onAutocompleteLoadEnd}>
                    <CustomTextField
                      type="text"
                      onChange={handleChange}
                      onBlur={(event, newValue) => {
                        handleBlur(event);
                      }}
                      variant="outlined"
                      placeholder="Enter Ending address"
                      fullWidth
                    />
                  </Autocomplete>
                </Grid>

                <Grid
                  item
                  lg={4}
                  md={6}
                  sm={6}
                  xs={12}
                  style={{ marginTop: "-10px" }}
                >
                  <CustomFormLabel htmlFor="end_pt_latitude">
                    Latitude <span style={{ color: "red" }}>*</span>
                  </CustomFormLabel>
                  <CustomTextField
                    id="end_pt_latitude"
                    name="end_pt_latitude"
                    type="text"
                    value={ending_latitude}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    variant="outlined"
                    // placeholder=" Final Destination Latitude"
                    fullWidth
                    InputProps={{ readOnly: true }}
                  />
                </Grid>

                <Grid
                  item
                  lg={4}
                  md={6}
                  sm={6}
                  xs={12}
                  style={{ marginTop: "-10px" }}
                >
                  <CustomFormLabel htmlFor="end_pt_longitude">
                    Longitude <span style={{ color: "red" }}>*</span>
                  </CustomFormLabel>
                  <CustomTextField
                    id="end_pt_longitude"
                    name="end_pt_longitude"
                    type="text"
                    value={ending_longitude}
                    variant="outlined"
                    // placeholder="Enter Final Destination Longitude"
                    fullWidth
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
              </Grid>

              <GoogleMap
                mapContainerStyle={{
                  height: "400px",
                  width: "100%",
                  marginTop: "20px",
                }}
                zoom={10}
                center={startMarker.position}
              >
                {directions && <DirectionsRenderer directions={directions} />}

                <Marker position={startMarker.position} />
                <Marker position={endMarker.position} />

                {viaPoints.map((viaPoint, index) => (
                  <Marker
                    key={index}
                    position={{
                      lat: parseFloat(viaPoint.via_latitude),
                      lng: parseFloat(viaPoint.via_longitude),
                    }}
                  />
                ))}
              </GoogleMap>
            </Grid>

            <Grid
              item
              lg={4}
              md={6}
              sm={6}
              xs={12}
              style={{ marginTop: "-10px" }}
            >
              <CustomFormLabel htmlFor="route_name">
                Route Name <span style={{ color: "red" }}>*</span>
              </CustomFormLabel>
              <CustomTextField
                id="route_name"
                name="route_name"
                type="text"
                value={values.route_name}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.route_name && Boolean(errors.route_name)}
                helperText={touched.route_name && errors.route_name}
                variant="outlined"
                placeholder="Enter Route Name"
                fullWidth
              />
            </Grid>

            <Grid
              item
              lg={4}
              md={6}
              sm={6}
              xs={12}
              style={{ marginTop: "-10px" }}
            >
              <CustomFormLabel htmlFor="distance">
                Total Distance (Km) <span style={{ color: "red" }}>*</span>
              </CustomFormLabel>
              <CustomTextField
                id="distance"
                name="distance"
                type="text"
                value={distance}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.distance && Boolean(errors.distance)}
                helperText={touched.distance && errors.distance}
                variant="outlined"
                placeholder="Total Distance"
                fullWidth
              />
            </Grid>

            <Grid
              item
              lg={4}
              md={6}
              sm={6}
              xs={12}
              style={{ marginTop: "-10px" }}
            >
              <CustomFormLabel htmlFor="time_duration">
                Total Duration (Min) <span style={{ color: "red" }}>*</span>
              </CustomFormLabel>
              <CustomTextField
                id="time_duration"
                name="time_duration"
                type="text"
                value={time_duration}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.time_duration && Boolean(errors.time_duration)}
                helperText={touched.time_duration && errors.time_duration}
                variant="outlined"
                placeholder="Total Duration in minute"
                fullWidth
              />
            </Grid>
            <Grid item lg={12} md={12} sm={12}>
              <Button type="submit" color="success" variant="contained">
                Submit
              </Button>
            </Grid>
          </Grid>
        </form>
      </ParentCard>
    </>
  );
};

export default AddNewRoute;
